
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useSearchParams } from "react-router-dom";
import Loading from "../../Common/Loading";
import { useNavigate } from "react-router-dom";


interface FamilyListItem {
  schoolCode: string;
  familyCode: string;
  login: string;
  activityStatus: string;
  rowColor: string;
  family: string;
  address: string;
  suburb: string;
  postcode: string;
  state: string;
  phone: string;
  dateOfSale: string;
  packages: string;
  installer: string;
  installDate: string;
  dongles: string;
}

interface DongleItem {
  dongleNo: string | number;
  damagedChecked: boolean;
  damagedDate: string;
  returnedChecked: boolean;
  returnedDate: string;
  replacedChecked: boolean;
  replacedDate: string;
  damagedDongleNo: string;
  remarks: string;
  isNew?: boolean;          // mirrors VB spDongle "NEW"/"OLD"
  replacementReadOnly?: boolean;
}

interface PackageItem {
  name: string;
  price: number;
}

interface Student {
  name: string;
  year: string;
  email: string;
  login: string;
}

interface Credit {
  addedBy: string;
  creditDate: string;
  notes: string;
  creditAmount: number;
}

interface Documents {
  purchaseOrder: boolean;
  crelogixContract: boolean;
  padForm: boolean;
  voidCheque: boolean;
  studentEdge: boolean;
  moneyBackGuarantee: boolean;
  acknowledgementForm: boolean;
  completedDeliveryDocket: boolean;
}

interface FamilyDetail {
  schoolCode: string;
  familyCode: string;
  login: string;
  loginId: string;
  password: string;
  activityStatus: string;
  installed: string;
  firstName: string;
  surname: string;
  email: string;
  email0: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  areaCode: string;
  phone: string;
  fatherName: string;
  fatherSurName: string;
  motherName: string;
  motherSurName: string;
  primaryContact: string;
  mobile1: string;
  mobile2: string;
  fatherOfficeAC: string;
  fatherOfficePhone: string;
  motherOfficeAC: string;
  motherOfficePhone: string;
  familyBackground: string;
  noOfStudents: string;
  school: string;
  dateOfSale: string;
  familyInfo: string;
  testimonials: string;
  referrals: string;
  cancelComments: string;
  specialInstruction: string;
  additionalDiscount: string;
  salesman: string;
  salesmanLogin: string;
  typeOfSaleValue: string;
  supportFee: string;
  freeSupportMonths: string;
  supportFeePayDate: string;
  orderDate: string;
  orderRef: string;
  deliveryDate: string;
  ddCode: string;
  onlineReporting: boolean;
  mailReporting: boolean;
  mailReportingAddress: string;
  hardlockNumber: string;
  dongleNos: string;
  listOfDongle: string;
  dlrOrHo: string;
  dongleVersion: boolean;
  webVersion: boolean;
  showDongles: boolean;
  dongles: DongleItem[];
  extraHardLocks: string;
  extraHardLocksPrice: string;
  defaultExtraHLPrice: number;
  computerPrice: string;
  computerIncluded: boolean;
  computerSO: string;
  displayRdoComp: string;
  consLeadOnly: boolean;
  consLeadAndAppt: boolean;
  isFinanceSale: string;
  getSaleType: string;
  getDepositPayOpt: string;
  getBalPayOpt: string;
  gst: number;
  pst: number;
  hst: number;
  gstDisplay: string;
  totalAmount: number;
  depositAmount: number;
  balanceAmount: number;
  totalIncTax: number;
  discountAmount: number;
  paymentContactDetails: string;
  paymentContact: string;
  displayPaymentDetails: boolean;
  canModifyPayment: boolean;
  packageIds: string;
  packageNames: string;
  packagePrices: string;
  documents: Documents;
  approved: string;
  saleApproved: boolean;
  famStatus: string;
  revCancelEnabled: boolean;
  showStaffCommission: boolean;
  displayModifyPkg: boolean;
  installationDate: string;
  installationTime: string;
  installerLogin: string;
  installStatus: string;
  installStatusCode: string;
  cpc: boolean;
  cancellationReason: string;
  isChina: boolean;
  students: Student[];
  credits: Credit[];
  totalCredit: number;
  hoFollowUp: boolean;
}

interface SelectOption {
  value: string;
  label: string;
  status?: string;
}

const API = process.env.REACT_APP_DOCUMENT_LINK || "";

// ─────────────────────────────────────────────────────────────────────
// Small helpers (mirrors VB JS functions)
// ─────────────────────────────────────────────────────────────────────

function isDecimalKey(e: React.KeyboardEvent): boolean {
  const k = e.keyCode || e.which;
  return (k >= 48 && k <= 57) || k === 8 || k === 46 || k === 190;
}

function isNumeralsOnly(e: React.KeyboardEvent): boolean {
  const k = (e as any).charCode || e.keyCode || e.which;
  return k >= 48 && k <= 57;
}

function isAlphaOnly(e: React.KeyboardEvent): boolean {
  const k = e.keyCode || e.which;
  return (k >= 65 && k <= 90) || (k >= 97 && k <= 122);
}

function isTimeKey(e: React.KeyboardEvent): boolean {
  const k = e.keyCode || e.which;
  return (k >= 48 && k <= 57) || k === 8 || k === 186 || k === 58;
}

function gfnTrim(s: string): string {
  return (s || "").trim();
}

function isValidDate(s: string): boolean {
  if (!s) return false;
  const parts = s.split("/");
  if (parts.length !== 3) return false;
  const [d, m, y] = parts.map(Number);
  if (!d || !m || !y) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

function isValidEmail(email: string): boolean {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function isValidTime(t: string): boolean {
  if (!t) return true;
  if (t.indexOf(":") !== 2) return false;
  const [h, m] = [parseInt(t.substring(0, 2)), parseInt(t.substring(3))];
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

function formatMoney(v: number): string {
  if (!v || v <= 0) return "0.00";
  return (Math.round(v * 100) / 100).toFixed(2);
}

function getTodayDate(): string {
  const today = new Date();
  const d = String(today.getDate()).padStart(2, "0");
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const y = today.getFullYear();
  return `${d}/${m}/${y}`;
}

function withTodayIfNull(value?: string): string {
  return value && value.trim() ? value : getTodayDate();
}

function parsePackages(names: string, prices: string): PackageItem[] {
  if (!names) return [];
  const nameArr = names.split(",");
  const priceArr = prices ? prices.split(",") : [];
  return nameArr
    .map((n, i) => ({ name: n.trim(), price: parseFloat(priceArr[i] || "0") || 0 }))
    .filter((p) => p.name);
}

// ─────────────────────────────────────────────────────────────────────
// Modal (mirrors gfn_ShowMessage)
// ─────────────────────────────────────────────────────────────────────

interface ModalState {
  msg: string;
  type: "A" | "E" | "Q" | "I";
  onOk?: () => void;
  onCancel?: () => void;
}

document.body.style.backgroundColor = "azure";


function Modal({ msg, type, onOk, onCancel }: ModalState) {
  return (
    <div
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.5)", zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff", border: "2px solid blue", padding: 20,
          minWidth: 320, fontFamily: "Arial", fontSize: 13,
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: 10 }}>
          {type === "E" ? "Error" : type === "Q" ? "Confirm" : "Message"}
        </div>
        <div style={{ marginBottom: 16 }}>{msg}</div>
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <button className="cssButton" onClick={onOk}>OK</button>
          {type === "Q" && onCancel && (
            <button className="cssButton" onClick={onCancel}>Cancel</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DocFlag – mirrors VB PurchaseOrder/CrelogixContract/etc. ✓/✗ display
// ─────────────────────────────────────────────────────────────────────

function DocFlag({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div style={{ fontSize: 11, marginBottom: 2 }}>
      {ok
        ? <img src="/Images/check.gif" alt="✓" style={{ verticalAlign: "middle" }} />
        : <b style={{ color: "red" }}>✗</b>
      }{" "}{label}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DongleTable – mirrors VB CreateTrsForDongles / fn_AddTrToDongleTable
// ─────────────────────────────────────────────────────────────────────

function DongleTable({
  rows,
  onChange,
  onValidate,
  onCheckDamaged,
  showAlert,
}: {
  rows: DongleItem[];
  onChange: (rows: DongleItem[]) => void;
  onValidate: (no: string) => Promise<boolean>;
  onCheckDamaged: (dongleNo: string, damagedNo: string) => Promise<boolean>;
  showAlert: (msg: string, type?: "A" | "E" | "Q" | "I") => void;
}) {
  const update = (i: number, patch: Partial<DongleItem>) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    onChange([
      ...rows,
      {
        dongleNo: "", damagedChecked: false, damagedDate: "",
        returnedChecked: false, returnedDate: "",
        replacedChecked: false, replacedDate: "",
        damagedDongleNo: "", remarks: "", isNew: true,
      },
    ]);
  };

  // fn_MoveDongleData – check damaged confirm
  const handleDamaged = (i: number, checked: boolean) => {
    if (!gfnTrim(String(rows[i].dongleNo))) return;
    if (checked) {
      if (window.confirm(
        "By ticking this box you have requested to mark the dongle as damaged.\n\n  Click OK to proceed, or No to cancel your request"
      )) {
        const today = new Date();
        const d = String(today.getDate()).padStart(2, "0");
        const m = String(today.getMonth() + 1).padStart(2, "0");
        const y = today.getFullYear();
        update(i, { damagedChecked: true, damagedDate: `${d}/${m}/${y}` });
      } else {
        update(i, { damagedChecked: false, damagedDate: "" });
      }
    } else {
      update(i, { damagedChecked: false, damagedDate: "" });
    }
  };

  // fn_MarkReturned
  const handleReturned = (i: number, checked: boolean) => {
    const today = new Date();
    const d = String(today.getDate()).padStart(2, "0");
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const y = today.getFullYear();
    update(i, {
      returnedChecked: checked,
      returnedDate: checked ? `${d}/${m}/${y}` : "",
    });
  };

  // fn_MarkReplaced
  const handleReplaced = (i: number, checked: boolean) => {
    const today = new Date();
    const d = String(today.getDate()).padStart(2, "0");
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const y = today.getFullYear();
    update(i, {
      replacedChecked: checked,
      replacedDate: checked ? `${d}/${m}/${y}` : "",
    });
  };

  const hTd: React.CSSProperties = {
    border: "1px solid black", background: "none",
    padding: "2px 4px", fontSize: 10, textAlign: "center",
  };

  return (
    <div>
      <div
        style={{
          padding: 2, background: "#AAD5FF", fontSize: 13,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          width: 600,
        }}
      >
        <b>Dongles</b>
        <div>
          <button className="cssButton" style={{ fontSize: 12, height: 25, border: "1px solid white", marginRight: 4 }} onClick={addRow}>
            Add
          </button>
        </div>
      </div>
      <div
        style={{
          width: 600, height: 200, overflowX: "scroll",
          border: "1px solid black", borderTop: "none",
        }}
      >
        <table id="tblManageDongles" cellPadding={2} cellSpacing={0}
          style={{ width: "100%", fontSize: 10, borderBottom: "1px solid black" }}>
          <thead style={{ backgroundColor: "none" }}>
            <tr>
              <td style={hTd}>SL NO.</td>
              <td style={hTd}>Dongle no.</td>
              <td style={hTd}>Damaged?</td>
              <td style={hTd}>Damaged Date</td>
              <td style={hTd}>Returned?</td>
              <td style={hTd}>Returned Date</td>
              <td style={hTd}>Replaced?</td>
              <td style={hTd}>Replaced Date</td>
              <td style={hTd}>Replacement Dongle</td>
              <td style={{ ...hTd, borderRight: "none" }}>Remarks</td>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const tdS: React.CSSProperties = { border: "1px solid black", padding: 2 };
              const rdOnly = row.replacementReadOnly;
              return (
                <tr key={i} style={{ textAlign: "left" }}>
                  <td style={{ ...tdS, textAlign: "center" }}>{i + 1}</td>
                  <td style={tdS}>
                    <input
                      id={`txtDongle${i + 1}`}
                      type="text"
                      value={String(row.dongleNo)}
                      maxLength={10}
                      style={{ width: 80, fontFamily: "Trebuchet MS", fontSize: 11, border: "1px solid #119ECC", paddingLeft: 3 }}
                      onKeyPress={(e) => { if (!isNumeralsOnly(e)) e.preventDefault(); }}
                      onBlur={async (e) => {
                        const v = e.target.value.trim();
                        if (v && row.isNew) {
                          const ok = await onValidate(v);
                          if (!ok) update(i, { dongleNo: "" });
                        }
                      }}
                      onChange={(e) => update(i, { dongleNo: e.target.value })}
                    />
                    <span id={`spDongle${i + 1}`} style={{ display: "none" }}>
                      {row.isNew ? "NEW" : "OLD"}
                    </span>
                  </td>
                  <td style={{ ...tdS, textAlign: "center" }}>
                    <input type="checkbox" id={`chkDamagedDongle${i + 1}`}
                      checked={row.damagedChecked}
                      onChange={(e) => handleDamaged(i, e.target.checked)} />
                  </td>
                  <td style={tdS}>
                    <input type="text" id={`txtDamagedDongleDate${i + 1}`}
                      value={row.damagedDate} maxLength={10}
                      style={{ width: 80, fontFamily: "Trebuchet MS", fontSize: 11, border: "1px solid #119ECC", paddingLeft: 3 }}
                      readOnly
                      onChange={(e) => update(i, { damagedDate: e.target.value })} />
                  </td>
                  <td style={{ ...tdS, textAlign: "center" }}>
                    <input type="checkbox" id={`chkReturned${i + 1}`}
                      checked={row.returnedChecked}
                      onChange={(e) => handleReturned(i, e.target.checked)} />
                  </td>
                  <td style={tdS}>
                    <input type="text" id={`txtReturnedDate${i + 1}`}
                      value={row.returnedDate} maxLength={10}
                      style={{ width: 80, fontFamily: "Trebuchet MS", fontSize: 11, border: "1px solid #119ECC", paddingLeft: 3 }}
                      readOnly
                      onChange={(e) => update(i, { returnedDate: e.target.value })} />
                  </td>
                  <td style={{ ...tdS, textAlign: "center" }}>
                    <input type="checkbox" id={`chkReplaced${i + 1}`}
                      checked={row.replacedChecked}
                      onChange={(e) => {
                        if (!row.replacedChecked && !e.target.checked) return;
                        handleReplaced(i, e.target.checked);
                      }} />
                  </td>
                  <td style={tdS}>
                    <input type="text" id={`txtReplacedDate${i + 1}`}
                      value={row.replacedDate} maxLength={10}
                      style={{ width: 80, fontFamily: "Trebuchet MS", fontSize: 11, border: "1px solid #119ECC", paddingLeft: 3 }}
                      readOnly
                      onChange={(e) => update(i, { replacedDate: e.target.value })} />
                  </td>
                  <td style={tdS}>
                    <input type="text" id={`txtReplacementDongleNo${i + 1}`}
                      value={row.damagedDongleNo} maxLength={10}
                      style={{ width: 80, fontFamily: "Trebuchet MS", fontSize: 11, border: "1px solid #119ECC", paddingLeft: 3 }}
                      readOnly={rdOnly}
                      className={rdOnly ? "cssReadOnly" : ""}
                      onKeyPress={(e) => {
                        if (!row.replacedChecked) {
                          showAlert("Please check 'Replaced'.", "A");
                          e.preventDefault();
                          return;
                        }
                        if (!isNumeralsOnly(e)) e.preventDefault();
                      }}
                      onChange={(e) => {
                        if (!rdOnly) update(i, { damagedDongleNo: e.target.value });
                      }} />
                  </td>
                  <td style={{ ...tdS, borderRight: "none" }}>
                    <textarea id={`txtRemarks${i + 1}`}
                      rows={1} cols={14} maxLength={100}
                      style={{ fontFamily: "Trebuchet MS", fontSize: 11, border: "1px solid #119ECC" }}
                      value={row.remarks}
                      onChange={(e) => update(i, { remarks: e.target.value })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────

interface Props {
  schoolCode?: string;
  familyCode?: string;
  login?: string;
  country?: string;
  category?: string;
  officeLogin?: string;
  supportSchoolCode?: string;
  supportFamilyCode?: string;
  supportLogin?: string;
  adminLogin?: string;
  fromCourtesyCall?: string;
  filterType?: string;
  previousPage?: string;
  isSalesman?: boolean;
  canCancel?: boolean;    // bit 77 of NewPermissions – mirrors VB T3010
}

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

export default function EL_SalesModify(props: Props) {
  const [searchParams] = useSearchParams();

  // URL params mirror VB QueryString
  const urlSchoolCode = searchParams.get("SchoolCode") || props.schoolCode || "";
  const urlFamilyCode = searchParams.get("FamilyCode") || props.familyCode || "";
  const urlLogin = searchParams.get("Login") || props.login || "";
  const urlCountry = searchParams.get("Country") || props.country || "";
  const urlPrevious = searchParams.get("Previous") || props.previousPage || "";
  const urlFromCC = searchParams.get("FromCourtesyCall") || props.fromCourtesyCall || "";
  const urlFilterType = searchParams.get("FilterType") || props.filterType || "";
  const urlS = searchParams.get("S") || "";
  const urlF = searchParams.get("F") || "";
  const urlL = searchParams.get("L") || "";

  // ── Display flags (mirrors VB Displaytbl1 / Displaytbl2) ──────────
  const [showSearchBar, setShowSearchBar] = useState(true);
  const [showModifyTable, setShowModifyTable] = useState(false);

  // ── Search fields ─────────────────────────────────────────────────
  const [srchSurname, setSrchSurname] = useState("All");
  const [srchPhone, setSrchPhone] = useState("All");
  const [srchCity, setSrchCity] = useState("All");
  const [srchPostcode, setSrchPostcode] = useState("All");
  const [srchInstDock, setSrchInstDock] = useState("All");

  // ── List / detail state ───────────────────────────────────────────
  const [families, setFamilies] = useState<FamilyListItem[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [detail, setDetail] = useState<FamilyDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // ── Form state (mirrors ASP.NET controls) ─────────────────────────
  const [form, setForm] = useState<Partial<FamilyDetail>>({});

  // ── Combo options ──────────────────────────────────────────────────
  const [typeOfSaleOptions, setTypeOfSaleOptions] = useState<SelectOption[]>([]);
  const [cancelReasonOptions, setCancelReasonOptions] = useState<SelectOption[]>([]);
  const [installerOptions, setInstallerOptions] = useState<SelectOption[]>([]);
  const [stateOptions, setStateOptions] = useState<SelectOption[]>([]);

  // ── Products ──────────────────────────────────────────────────────
  const [packages, setPackages] = useState<PackageItem[]>([]);

  // ── Dongle table ──────────────────────────────────────────────────
  const [dongleRows, setDongleRows] = useState<DongleItem[]>([]);

  // ── Financial fields (mirrors VB BalanceStr inputs) ───────────────
  const [totalExec, setTotalExec] = useState(0);
  const [totalIncTax, setTotalIncTax] = useState(0);
  const [depositAmt, setDepositAmt] = useState(0);
  const [balanceAmt, setBalanceAmt] = useState(0);
  const [discountAmt, setDiscountAmt] = useState(0);
  const [additionalDiscount, setAdditionalDiscount] = useState("");

  // ── UI state ──────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [selectedRow, setSelectedRow] = useState("");
  const [modal, setModal] = useState<ModalState | null>(null);

  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate()

  const isDisabled = false; // change this condition as needed

  useEffect(() => {
    document.body.style.backgroundColor = "azure";
    document.body.style.overflowY = "hidden"
  }, []);

  const showAlert = useCallback(
    (msg: string, type: "A" | "E" | "Q" | "I" = "A",
      onOk?: () => void, onCancel?: () => void) => {
      setModal({
        msg, type,
        onOk: onOk || (() => setModal(null)),
        onCancel: onCancel || (() => setModal(null)),
      });
    },
    []
  );

  // ── Body background (matches VB page style) ───────────────────────
  useEffect(() => { document.body.style.fontFamily = "Arial"; }, []);

  // ── Load combos on mount ──────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}api/type-of-sale/`)
      .then((r) => r.json()).then((d) => setTypeOfSaleOptions(d.options || []));
    fetch(`${API}api/cancel-reasons/`)
      .then((r) => r.json()).then((d) => setCancelReasonOptions(d.options || []));
    fetch(`${API}api/installers_options/`)
      .then((r) => r.json()).then((d) => setInstallerOptions(d.options || []));
  }, []);

  // ── Load states when country changes (Fun_LoadCombo ddlStateCode) ─
  useEffect(() => {
    const c = (form.country || urlCountry || "").trim();

    if (!c || c.toUpperCase() === "CHINA") return;

    fetch(
      `${API}api/state_option_sales/?country=${encodeURIComponent(c)}`
    )
      .then((r) => r.json())
      .then((d) => setStateOptions(d.options || []))
      .catch((err) => console.error("Error fetching states:", err));
  }, [form.country, urlCountry]);

  // ── Auto-load when URL params present (mirrors hidPostBack.Value="Y") ─
  useEffect(() => {
    if (urlSchoolCode && urlFamilyCode && urlLogin) {
      loadFamilyDetail(urlSchoolCode, urlFamilyCode, urlLogin, urlCountry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // handleSearch  →  ListOfFamilies()
  // ─────────────────────────────────────────────────────────────────


  const handleRowsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (
    direction: "first" | "prev" | "next" | "last"
  ) => {
    switch (direction) {
      case "first":
        setCurrentPage(1);
        break;

      case "prev":
        setCurrentPage((p) => Math.max(1, p - 1));
        break;

      case "next":
        setCurrentPage((p) => Math.min(totalPages, p + 1));
        break;

      case "last":
        setCurrentPage(totalPages);
        break;
    }
  };

  const handleSearch = useCallback(async () => {
    setLoadingList(true);
    setFamilies([]);
    setShowModifyTable(false);
    try {
      const params = new URLSearchParams({
        school_code: sessionStorage.getItem("school_code") || urlSchoolCode,
        country: sessionStorage.getItem("country") || urlCountry,
        surname: srchSurname,
        phone: srchPhone,
        city: srchCity,
        postcode: srchPostcode,
        inst_dock_no: srchInstDock,
        category: props.category || "",
        office_login: props.officeLogin || "",
        is_salesman: props.isSalesman ? "Y" : "",
        salesman_login: props.login || "",
      });
      const resp = await fetch(`${API}api/families/?${params}`);
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      setFamilies(data.families || []);
      setCurrentPage(1);
    } catch (e) {
      showAlert("Error loading families. " + String(e), "E");
    } finally {
      setLoadingList(false);
    }
  }, [srchSurname, srchPhone, srchCity, srchPostcode, srchInstDock, props, showAlert, urlSchoolCode, urlCountry]);

  // ─────────────────────────────────────────────────────────────────
  // loadFamilyDetail  →  PopulateFamily()
  // ─────────────────────────────────────────────────────────────────

  const loadFamilyDetail = useCallback(
    async (sc: string, fc: string, lg: string, ct: string) => {
      setLoadingDetail(true);
      try {
        const params = new URLSearchParams({
          school_code: sc, family_code: fc, login: lg, country: ct,
        });
        const resp = await fetch(`${API}api/family_SM/?${params}`);
        const data = await resp.json();
        if (data.error) throw new Error(data.error);

        const d: FamilyDetail = data.family;
        setDetail(d);
        setForm({
          ...d,
          dateOfSale: withTodayIfNull(d.dateOfSale),
          installationDate: withTodayIfNull(d.installationDate),
          supportFeePayDate: withTodayIfNull(d.supportFeePayDate),
        });

        // ConstructProductsTable
        const pkgs = parsePackages(d.packageNames, d.packagePrices);
        setPackages(pkgs);

        // Financial fields
        setTotalExec(d.totalAmount);
        setTotalIncTax(d.totalIncTax);
        setDepositAmt(d.depositAmount);
        setBalanceAmt(d.balanceAmount);
        setDiscountAmt(d.discountAmount);
        setAdditionalDiscount(d.additionalDiscount || "");

        // Dongle rows
        setDongleRows(d.dongles || []);

        setShowSearchBar(false);
        setShowModifyTable(true);
        setSuccessMsg("");
      } catch (e) {
        showAlert("Error loading family: " + String(e), "E");
      } finally {
        setLoadingDetail(false);
      }
    },
    [showAlert]
  );

  // ─────────────────────────────────────────────────────────────────
  // fn_SetBalanceAmount_CA  (mirrors VB JS exactly)
  // ─────────────────────────────────────────────────────────────────

  const recalcBalance = useCallback(() => {
    if (!detail) return;
    const gst = detail.gst / 100;
    const pst = detail.pst / 100;
    const hst = detail.hst / 100;

    const pkgTotal = packages.reduce((s, p) => s + p.price, 0);
    const extraHLPrice = parseFloat(form.extraHardLocksPrice || "0") || 0;
    const compPrice = (form.computerIncluded)
      ? (parseFloat(form.computerPrice || "0") || 0)
      : 0;
    const addDisc = parseFloat(additionalDiscount || "0") || 0;

    // mirrors VB: totalProdPrice = pkgTotal - discount + extraHLPrice
    const execTotal = pkgTotal + extraHLPrice + compPrice - discountAmt - addDisc;
    const tax = execTotal * (gst + pst + hst);
    const incTax = execTotal + tax;
    const bal = incTax - depositAmt;

    setTotalExec(Math.round(execTotal * 100) / 100);
    setTotalIncTax(Math.round(incTax * 100) / 100);
    setBalanceAmt(Math.round(bal * 100) / 100);
  }, [detail, packages, form.extraHardLocksPrice, form.computerPrice, form.computerIncluded, discountAmt, depositAmt, additionalDiscount]);

  useEffect(() => { recalcBalance(); },
    [additionalDiscount, packages, form.extraHardLocksPrice, form.computerPrice, form.computerIncluded]);

  // ─────────────────────────────────────────────────────────────────
  // fn_ShowTD  (mirrors VB: show/hide dongle table on checkbox change)
  // ─────────────────────────────────────────────────────────────────

  const handleDongleVersionChange = (checked: boolean) => {
    setForm((f) => ({ ...f, dongleVersion: checked, showDongles: checked }));
    recalcBalance();
  };

  // fn_SetSubrub
  const handleStateChange = (val: string) => {
    setForm((f) => ({ ...f, state: val, city: "", postalCode: "" }));
  };

  // ─────────────────────────────────────────────────────────────────
  // fn_DisplayControls (Online/Mail reporting)
  // ─────────────────────────────────────────────────────────────────

  const handleReportingChange = (type: "Email" | "Address") => {
    if (type === "Email") {
      setForm((f) => ({
        ...f, onlineReporting: !f.onlineReporting, mailReporting: false,
      }));
    } else {
      setForm((f) => {
        const newMailRep = !f.mailReporting;
        let addr = f.mailReportingAddress || "";
        if (newMailRep && !addr) {
          addr = [f.address, f.city, f.state, f.postalCode].filter(Boolean).join(" ");
        }
        return {
          ...f, mailReporting: newMailRep, onlineReporting: false,
          mailReportingAddress: addr,
        };
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // Validate dongle  (mirrors VB fn_ValidateDongleNumber)
  // ─────────────────────────────────────────────────────────────────

  const validateDongle = async (dongleNo: string): Promise<boolean> => {
    if (!detail) return false;
    try {
      const params = new URLSearchParams({
        dongle_no: dongleNo,
        school_code: detail.schoolCode,
        dlr_or_ho: detail.dlrOrHo,
      });
      const resp = await fetch(`${API}api/validate-dongle/?${params}`);
      const data = await resp.json();
      if (data.result === "Y") return true;
      showAlert(data.message || "Invalid dongle number.", "A");
      return false;
    } catch {
      showAlert("Error validating dongle.", "E");
      return false;
    }
  };

  const checkDongleDamaged = async (dongleNo: string, damagedNo: string): Promise<boolean> => {
    const params = new URLSearchParams({ dongle_no: dongleNo, damaged_dongle_no: damagedNo });
    const resp = await fetch(`${API}api/check-dongle-damaged/?${params}`);
    const data = await resp.json();
    return data.isDamaged === true;
  };

  // ─────────────────────────────────────────────────────────────────
  // fn_SaveDatails  →  handleSave  (mirrors VB JS + btnSave_Click)
  // ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!detail || !form) return;

    const country = detail.country || "";

    // ── Client-side validation (mirrors VB fn_SaveDatails exactly) ──
    if (country.toUpperCase() !== "CHINA") {
      if (!gfnTrim(form.surname || "")) {
        showAlert("Enter Surname", "A"); return;
      }
      if (!gfnTrim(form.address || "")) {
        showAlert("Enter Address", "A"); return;
      }
      if (!gfnTrim(form.city || "")) {
        showAlert("Enter Suburb", "A"); return;
      }
    }

    if (gfnTrim(form.email0 || "") && !isValidEmail(form.email0 || "")) {
      showAlert("Please enter valid email ID", "A"); return;
    }

    if (gfnTrim(form.supportFeePayDate || "")) {
      if (!isValidDate(form.supportFeePayDate || "")) {
        showAlert("Invalid Support Fee Payment Date", "A"); return;
      }
    }
    if (gfnTrim(form.dateOfSale || "")) {
      if (!isValidDate(form.dateOfSale || "")) {
        showAlert("Invalid DateOfSale Date", "A"); return;
      }
    }
    if (gfnTrim(form.installationDate || "")) {
      if (!isValidDate(form.installationDate || "")) {
        showAlert("Invalid Date. format is 'dd/MM/yyyy'.", "A"); return;
      }
    }
    if (gfnTrim(form.installationTime || "")) {
      if (!isValidTime(form.installationTime || "")) {
        showAlert("Invalid format. eg: '23:59'.", "A"); return;
      }
    }

    // Type of product validation
    if (!form.dongleVersion && !form.webVersion) {
      showAlert("Please select Type of product - online or Dongle", "A");
      return;
    }

    if (form.familyInfo && form.familyInfo.length > 50) {
      showAlert("Length cannot be more than 50 characters", "A"); return;
    }

    // Build dongle detail string (mirrors VB fn_SaveDongleDetails inside fn_SaveDatails)
    // (Dongle saving is done separately via saveDongles API)

    setSaving(true);
    try {
      // Collect student details (mirrors VB hidStudentDetails)
      let studentDetails = "";
      dongleRows.forEach((_r, _i) => { /* no-op – dongles saved separately */ });

      const body = {
        schoolCode: detail.schoolCode,
        familyCode: detail.familyCode,
        login: detail.login,
        country: detail.country,
        supportSchoolCode: props.supportSchoolCode || "",
        supportFamilyCode: props.supportFamilyCode || "",
        supportLogin: props.supportLogin || "",
        adminLogin: props.adminLogin || "",
        category: props.category || "",
        firstName: form.firstName || "",
        surname: form.surname || "",
        email0: form.email0 || "",
        address: form.address || "",
        suburb: form.city || "",
        suburbChina: form.city || "",
        chinaState: form.state || "",
        state: form.state || "",
        postalCode: form.postalCode || "",
        areaCode: form.areaCode || "",
        phone: form.phone || "",
        noOfStudents: form.noOfStudents || "1",
        fatherName: form.fatherName || "",
        fatherSurName: form.fatherSurName || "",
        motherName: form.motherName || "",
        motherSurName: form.motherSurName || "",
        primaryContact: form.primaryContact || "",
        mobile1: form.mobile1 || "",
        mobile2: form.mobile2 || "",
        fatherOfficeAC: form.fatherOfficeAC || "",
        fatherOfficePhone: form.fatherOfficePhone || "",
        motherOfficeAC: form.motherOfficeAC || "",
        motherOfficePhone: form.motherOfficePhone || "",
        familyInfo: form.familyInfo || "",
        testimonials: form.testimonials || "",
        referrals: form.referrals || "",
        dateOfSale: withTodayIfNull(form.dateOfSale),
        specialInstruction: form.specialInstruction || "",
        installationDate: withTodayIfNull(form.installationDate),
        installationTime: form.installationTime || "",
        installerLogin: form.installerLogin || "",
        cpc: form.cpc || false,
        cancellationReason: form.cancellationReason || "",
        dongleVersion: form.dongleVersion || false,
        webVersion: form.webVersion || false,
        typeOfSale: form.typeOfSaleValue || "",
        supportFee: form.supportFee || "",
        freeSupportMonths: form.freeSupportMonths || "",
        supportFeePayDate: withTodayIfNull(form.supportFeePayDate),
        school: form.school || "",
        onlineReporting: form.onlineReporting || false,
        mailReporting: form.mailReporting || false,
        mailReportingAddress: form.mailReportingAddress || "",
        additionalDiscount,
        packageIds: form.packageIds || "",
        packageNames: form.packageNames || "",
        packagePrices: form.packagePrices || "",
        subjectsSelected: form.packageNames || "",
        packageSubjects: form.packageIds || "",
        paymentContactDetails: form.paymentContactDetails || "",
        extraHardLocks: form.extraHardLocks || "",
        extraHardLocksPrice: form.extraHardLocksPrice || "",
        totalAmount: totalExec,
        totalIncTax: totalIncTax,
        balanceAmount: balanceAmt,
        isPackageChange: false,   // TODO: track package changes
        studentDetails,
        cancelComments: form.cancelComments || "",
      };

      const resp = await fetch(`${API}api/save/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (data.error) throw new Error(data.error);

      setSuccessMsg("Successfully Modified!");
      // Reload (mirrors VB PopulateFamily() after save)
      await loadFamilyDetail(detail.schoolCode, detail.familyCode, detail.login, detail.country);
    } catch (e) {
      showAlert("Save failed: " + String(e), "E");
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // fn_SaveDongleDetails  →  handleSaveDongles
  // ─────────────────────────────────────────────────────────────────

  const handleSaveDongles = async () => {
    if (!detail) return;

    // Validation (mirrors VB fn_SaveDongleDetails)
    for (let i = 0; i < dongleRows.length; i++) {
      const row = dongleRows[i];
      if (!gfnTrim(String(row.dongleNo))) {
        showAlert("Please enter dongle no.", "A"); return;
      }
      if (row.remarks && row.remarks.indexOf("&") >= 0) {
        showAlert('Please donot use "&" character.', "A"); return;
      }
      if (row.returnedChecked && row.returnedDate && !row.remarks) {
        showAlert("Please enter remarks", "A"); return;
      }
      if (row.replacedChecked) {
        if (!row.replacedDate) {
          showAlert("Please enter replaced date. Format (eg. dd/MM/yyyy)", "A"); return;
        }
        if (!isValidDate(row.replacedDate)) {
          showAlert("Invalid date format. (eg. dd/MM/yyyy)", "A"); return;
        }
        if (!row.damagedDongleNo) {
          showAlert("Please enter replacement dongle number.", "A"); return;
        }
      }
      if (!row.replacedChecked && row.damagedDongleNo) {
        showAlert("Please check 'Replaced'.", "A"); return;
      }
    }

    // Build the string (mirrors VB StrDongleDetails format)
    const splitter1 = "^|^";
    const rowSpltr = "-$@^-";
    const dongleParts: string[] = [];
    const allDongles: string[] = [];
    const repDongles: string[] = [];

    dongleRows.forEach((row) => {
      const dn = String(row.dongleNo);
      allDongles.push(dn);
      if (row.damagedDongleNo && row.replacedChecked) repDongles.push(row.damagedDongleNo);

      let part = dn + splitter1;
      part += (row.damagedChecked ? withTodayIfNull(row.damagedDate) : "") + splitter1;
      part += (row.returnedChecked ? withTodayIfNull(row.returnedDate) : "") + splitter1;
      part += (row.replacedChecked ? withTodayIfNull(row.replacedDate) : "") + splitter1;
      part += (row.damagedDongleNo || "") + splitter1;
      part += (row.remarks || "") + splitter1;
      part += (row.isNew ? "NEW" : "OLD") + splitter1;
      part += (row.replacedChecked && row.damagedDongleNo && !row.replacementReadOnly ? "Y" : "N");
      dongleParts.push(part);
    });

    setSaving(true);
    try {
      const resp = await fetch(`${API}api/save-dongles/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolCode: detail.schoolCode,
          familyCode: detail.familyCode,
          login: detail.login,
          isHO: detail.dlrOrHo,
          ddCode: detail.ddCode,
          donglesStr: dongleParts.join(rowSpltr),
          rdStr: repDongles.join(","),
        }),
      });
      const data = await resp.json();
      if (data.result !== "Y") throw new Error(data.error || "Save failed");
      showAlert("Successfully saved.", "A");

      // Mark new dongles as OLD (mirrors VB)
      setDongleRows((prev) =>
        prev.map((r) => ({ ...r, isNew: false }))
      );
    } catch (e) {
      showAlert(String(e), "E");
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // fn_CancelFamily  →  handleCancel
  // ─────────────────────────────────────────────────────────────────

  const handleCancel = () => {
    if (!gfnTrim(form.cancelComments || "")) {
      showAlert("Please enter Comments before cancellation", "A"); return;
    }
    if (!window.confirm("This action will cancel the family")) return;
    _doCancel();
  };

  const _doCancel = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const resp = await fetch(`${API}api/cancel/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolCode: detail.schoolCode,
          familyCode: detail.familyCode,
          login: detail.login,
          cancelComments: form.cancelComments || "",
          dongleNos: detail.dongleNos,
          supportSchoolCode: props.supportSchoolCode || "",
          supportFamilyCode: props.supportFamilyCode || "",
          supportLogin: props.supportLogin || "",
          adminLogin: props.adminLogin || "",
          mSchoolCode: props.schoolCode || "",
          mFamilyCode: props.familyCode || "",
          mLogin: props.login || "",
        }),
      });
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      await loadFamilyDetail(detail.schoolCode, detail.familyCode, detail.login, detail.country);
    } catch (e) {
      showAlert("Cancel failed: " + String(e), "E");
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // fn_ConfirmRevCancel  →  handleRevCancel
  // ─────────────────────────────────────────────────────────────────

  const handleRevCancel = () => {
    showAlert("Do you want to Reverse Cancel this sale?", "Q",
      async () => {
        setModal(null);
        if (!detail) return;
        setSaving(true);
        try {
          const resp = await fetch(`${API}api/reverse-cancel/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              schoolCode: detail.schoolCode,
              familyCode: detail.familyCode,
              login: detail.login,
              dongleNos: detail.dongleNos,
              supportSchoolCode: props.supportSchoolCode || "",
              supportFamilyCode: props.supportFamilyCode || "",
              supportLogin: props.supportLogin || "",
              adminLogin: props.adminLogin || "",
              category: props.category || "",
            }),
          });
          const data = await resp.json();
          if (data.error) throw new Error(data.error);
          await loadFamilyDetail(detail.schoolCode, detail.familyCode, detail.login, detail.country);
        } catch (e) {
          showAlert("Reverse cancel failed: " + String(e), "E");
        } finally {
          setSaving(false);
        }
      },
      () => setModal(null)
    );
  };

  // ─────────────────────────────────────────────────────────────────
  // fn_Back
  // ─────────────────────────────────────────────────────────────────

  const handleBack = () => {
    if (urlPrevious) {
      let url =
        `${urlPrevious}?SchoolCode=${detail?.schoolCode || ""}` +
        `&FamilyCode=${detail?.familyCode || ""}` +
        `&Login=${detail?.login || ""}`;

      if (urlFilterType) {
        url += `&FilterType=${encodeURIComponent(urlFilterType)}`;
      }

      if (urlFromCC) {
        url += `&FromCourtesyCall=${encodeURIComponent(urlFromCC)}`;
      }

      if (urlS) {
        url += `&S=${encodeURIComponent(urlS)}`;
      }

      if (urlF) {
        url += `&F=${encodeURIComponent(urlF)}`;
      }

      if (urlL) {
        url += `&L=${encodeURIComponent(urlL)}`;
      }

      navigate(
        {
          pathname: urlPrevious,
          search: url.substring(url.indexOf("?"))
        },
        {
          replace: true
        }
      );
      return;
    }

    setShowSearchBar(true);
    setShowModifyTable(false);
    setDetail(null);
    setForm({});
    setSuccessMsg("");
  };

  const handleNewSale = () => {
    const sc = sessionStorage.getItem("school_code") || "";
    window.location.href = `/SALES/NewSales/EL_CheckCMS?schoolCode=${sc}`;
  };

  // ─────────────────────────────────────────────────────────────────
  // fn_GetFamilyFormsStatus (Pre-Audit popup)
  // ─────────────────────────────────────────────────────────────────

  const openPreAudit = () => {
    if (!detail) return;
    const url = `/Sales/EL_PreAudit?SchoolCode=${detail.schoolCode}&FamilyCode=${detail.familyCode}&Login=${detail.login}`;
    window.open(url, "PreAudit", "H=700,W=800");
  };

  // fn_GetPaymentFinanceOption
  const openModifyPayment = () => {
    if (!detail) return;
    const url = `/Sales/EL_ModifyPaymentMethod?SchoolCode=${detail.schoolCode}&FamilyCode=${detail.familyCode}&Login=${detail.login}&BalanceAmount=${balanceAmt}`;
    window.open(url, "ModifyPayment", "H=700,W=700");
  };

  // fn_HardlockNo
  const openHardlock = () => {
    if (!detail) return;
    const url = `/Sales/KM_Pop_Hardlock?FamilyCode=${detail.familyCode}&DDcode=${detail.ddCode}`;
    window.open(url, "Hardlocks", "H=350,W=300");
  };

  // fn_AddEditStudentDetails
  const openFamilyUsers = () => {
    if (!detail) return;

    const width = 700;
    const height = 1000;

    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = 0;

    const url = `/DEALER/EL_EditFamilyProfile?SchoolCode=${detail.schoolCode}&FamilyCode=${detail.familyCode}&Login=${detail.login}`;

    window.open(
      url,
      "FamilyUsers",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };
  // fn_PrintDeliveryDocket
  const printDeliveryDocket = () => {
    if (!detail) return;
    const url = `/Sales/NewSales/EL_Print_DeliveryDockets?SchoolCode=${detail.schoolCode}&FamilyCode=${detail.familyCode}&Login=${detail.login}&DDCode=${detail.ddCode}`;
    window.open(url, "PrintDocket", "H=768,W=800");
  };

  // fn_SelectProducts
  const openSelectPackages = () => {
    if (!detail) return;
    const url = `/Sales/NewSales/EL_SelectPackages?ProdIDs=${form.packageIds || ""}&SchoolCode=${detail.schoolCode}`;
    const win = window.open(url, "SelectPackages", "H=700,W=600");

    if (win) {
      win.focus();
    }
  };

  const openSuburbSelect = () => {
    if (!detail) return;

    const popup = window.open(
      `/SALES/EL_SelectSuburb?Country=${encodeURIComponent(form.country || detail.country)}&StateCode=${encodeURIComponent(form.state || "")}&StateName=${encodeURIComponent(form.state || "")}`,
      "SelectSuburb",
      "width=900,height=650,resizable=yes,scrollbars=yes"
    );

    if (!popup) return;

    const timer = setInterval(() => {

      if (popup.closed) {

        clearInterval(timer);

        const ret = (window as any).ret;

        if (ret) {

          const [suburb, postcode] = ret.split("|");

          setForm(prev => ({
            ...prev,
            city: suburb,
            postalCode: postcode,
          }));

          (window as any).ret = "";
        }

      }

    }, 200);
  };

  // ─────────────────────────────────────────────────────────────────
  // Patch helper
  // ─────────────────────────────────────────────────────────────────

  const patch = (field: keyof FamilyDetail, value: unknown) =>
    setForm((f) => ({ ...f, [field]: value }));

  // ─────────────────────────────────────────────────────────────────
  // Derived booleans (mirrors VB Installed, ActivityStatus checks)
  // ─────────────────────────────────────────────────────────────────

  const isInstalled = (detail?.installed || "") === "1";
  const isCancelled = (form.activityStatus || detail?.activityStatus) === "C";
  const isChina = (form.country || detail?.country || "").toUpperCase() === "CHINA";
  const isAustralia = (form.country || detail?.country || "").toUpperCase() === "AUSTRALIA";
  // VB: ddlInstaller.Enabled = False for Australia (T2121)
  const installerDisabled = isAustralia;
  const cpcDisabled = isAustralia;
  const instDateDisabled = isAustralia;

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────

  const readOnlyCss: React.CSSProperties = {
    backgroundColor: "#e2e2e2", paddingLeft: 2,
  };


  const totalPages = Math.max(1, Math.ceil(families.length / rowsPerPage));

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const currentFamilies = families.slice(startIndex, endIndex);

  return (
    <>
      {modal && (
        <Modal
          msg={modal.msg} type={modal.type}
          onOk={modal.onOk || (() => setModal(null))}
          onCancel={modal.onCancel}
        />
      )}
      <div style={{ textAlign: "center", color: "navy", margin: "4px", fontWeight: "bold", fontSize: "13pt" }}> Kinetic Education</div>

      {/* Please-wait overlay (mirrors divMesg / divOpaque) */}
      {saving && <Loading />}

      <div
        id="divMain"
        className="cssBody"
        style={{
          margin: 0,
          padding: 2,
          fontFamily: "Arial",
          fontSize: 12,
          height: "97vh",
          overflowY: "auto",
          paddingBottom: "70px",
        }}
      >

        {/* ── tbl1: Search bar ──────────────────────────────────── */}
        {showSearchBar && (
          <table border={0} cellPadding={3} cellSpacing={0}
            style={{ width: "100%", paddingBottom: 3, border: "1px solid black" }}>
            <tbody>
              <tr>
                <td width="15%">&nbsp;Surname</td>
                <td width="15%">&nbsp;Phone No.</td>
                <td width="15%">&nbsp;Suburb</td>
                <td width="15%">&nbsp;PostCode</td>
                <td width="15%">&nbsp;Inst.Docket</td>
                <td width="8%">&nbsp;</td>
                <td width="8%">&nbsp;</td>
                <td width="8%">&nbsp;</td>
              </tr>
              <tr>
                <td>
                  <input className="cssTextBox" style={{ width: 100 }} maxLength={50}
                    value={srchSurname} onChange={(e) => setSrchSurname(e.target.value)} />
                </td>
                <td>
                  <input className="cssTextBox" style={{ width: 100 }} maxLength={25}
                    value={srchPhone} onChange={(e) => setSrchPhone(e.target.value)} />
                </td>
                <td>
                  <input className="cssTextBox" style={{ width: 100 }} maxLength={25}
                    value={srchCity} onChange={(e) => setSrchCity(e.target.value)} />
                </td>
                <td>
                  <input className="cssTextBox" style={{ width: 100 }} maxLength={50}
                    value={srchPostcode} onChange={(e) => setSrchPostcode(e.target.value)} />
                </td>
                <td>
                  <input className="cssTextBox" style={{ width: 100 }}
                    value={srchInstDock} onChange={(e) => setSrchInstDock(e.target.value)} />
                </td>
                <td />
                <td>
                  <button className="cssButton"
                    style={{ width: 100, fontWeight: "bold", height: 22, fontSize: "9pt" }}
                    onClick={handleNewSale}>
                    New Sale
                  </button>
                </td>
                <td>
                  <button className="cssButton"
                    style={{ width: 70, fontWeight: "bold", height: 22, fontSize: "9pt" }}
                    onClick={handleSearch} disabled={loadingList}>
                    {loadingList ? "..." : " Go "}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {/* ── Family list ───────────────────────────────────────── */}
        {showSearchBar && families.length > 0 && (
          <div id="lst_divScrollableTable"
            style={{ overflowY: "auto", maxHeight: 700, marginTop: 4 }}>
            <table cellPadding={2} cellSpacing={0}
              style={{ width: "100%", fontSize: "9pt", fontFamily: "Arial", border: "1px solid #80a0ff", borderTop: "none" }}>
              <thead>
                <tr>
                  <th
                    colSpan={12}
                    style={{
                      backgroundColor: "#c7ddfd",
                      color: "#2859ac",
                      textAlign: "center",

                      fontSize: "8pt",
                      padding: "6px",
                      border: "1px solid #80a0ff"
                    }}
                  >
                    List of Family
                  </th>
                </tr>

                <tr style={{ backgroundColor: "#c7ddfd", fontWeight: "normal", color: "#2859ac", padding: "8px", border: "1px solid #80a0ff" }}>
                  {[
                    "#",
                    "Family",
                    "Address",
                    "Suburb",
                    "Postcode",
                    "State",
                    "Phone",
                    "Date of Sale",
                    "Packages",
                    "Installer",
                    "Install Date",
                    "Dongles"
                  ].map((h) => (
                    <th key={h} align="left" style={{ padding: "8px 4px", fontWeight: "normal", border: "1px solid #80a0ff", borderTop: 'none', borderRight: "none" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentFamilies.map((fam, index) => {
                  const key = `${fam.schoolCode}|${fam.familyCode}|${fam.login}`;
                  return (
                    <tr
                      key={key}
                      style={{
                        cursor: "pointer",
                        color: fam.rowColor,
                        backgroundColor:
                          selectedRow === key
                            ? "orange"
                            : index % 2 === 0
                              ? "#f4faff"
                              : "#ffffff",
                        border: "1px solid #80a0ff"
                      }}
                      onClick={() => {
                        setSelectedRow(key);
                        loadFamilyDetail(
                          fam.schoolCode,
                          fam.familyCode,
                          fam.login,
                          sessionStorage.getItem("country") || urlCountry || ""
                        );
                      }}
                    >
                      <td style={{ textAlign: "center", padding: "8px", borderRight: "1px solid #80a0ff" }}>{startIndex + index + 1}</td>
                      <td style={{ textDecoration: "underline", padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.family}</td>
                      <td style={{ padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.address}</td>
                      <td style={{ padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.suburb}</td>
                      <td style={{ padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.postcode}</td>
                      <td style={{ padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.state}</td>
                      <td style={{ padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.phone}</td>
                      <td style={{ padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.dateOfSale}</td>
                      <td style={{ padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.packages}</td>
                      <td style={{ padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.installer}</td>
                      <td style={{ padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.installDate}</td>
                      <td style={{ padding: "8px", borderRight: "1px solid #80a0ff" }}>{fam.dongles}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
                marginTop: "20px",
                fontSize: "9pt",
                fontFamily: "Arial",
                backgroundColor: "#c7ddfd",
                height: "35px",
                border: "2px solid #80a0ff",
              }}
            >
              <span style={{ color: "#2859ac" }}>Rows per page:</span>

              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>

              <button onClick={handleSearch}>
                Refresh
              </button>

              <button onClick={() => handlePageChange("first")}>
                &lt;&lt;
              </button>

              <button onClick={() => handlePageChange("prev")}>
                &lt;
              </button>

              <span>
                {currentPage} of {totalPages}
              </span>

              <button onClick={() => handlePageChange("next")}>
                &gt;
              </button>

              <button onClick={() => handlePageChange("last")}>
                &gt;&gt;
              </button>
            </div>
          </div>


        )}

        {loadingDetail && <Loading />}

        {/* ── tblModifyTable ────────────────────────────────────── */}
        {showModifyTable && detail && (
          <>
            {/* FamStatus */}
            {detail.famStatus && (
              <table border={1} cellPadding={2} cellSpacing={0} style={{ width: "98%" }}>
                <tbody>
                  <tr>
                    <td colSpan={2}>
                      <span style={{ color: "red", fontWeight: "bold" }}>
                        {detail.famStatus}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            )}

            {/* SuccessMSG */}
            {successMsg && (
              <span style={{ color: "blue", fontWeight: "bold" }}>
                {successMsg}
              </span>
            )}

            <table id="tblModifyTable" border={1} cellPadding={2} cellSpacing={0}
              style={{ width: "98%", overflowY: "scroll", fontSize: "10pt" }}>
              <tbody>
                {/* ── Top info row ─────────────────────────────── */}
                <tr>
                  <td colSpan={2}>
                    <table width="98%" border={0} cellSpacing={2} cellPadding={0}>
                      <tbody>
                        <tr>
                          <td width="20%" style={{ whiteSpace: "nowrap" }}>
                            &nbsp;Sale Type:&nbsp;
                            <span style={{ color: "#409FFF" }}>{detail.getSaleType}</span>
                          </td>
                          <td width="20%" style={{ whiteSpace: "nowrap" }}>
                            &nbsp;Deposit Payment Option:&nbsp;
                            <span style={{ color: "#409FFF" }}>{detail.getDepositPayOpt}</span>
                          </td>
                          <td width="25%" style={{ whiteSpace: "nowrap" }}>
                            &nbsp;Different Contact for Payment:&nbsp;
                            {detail.displayPaymentDetails && (
                              <b>
                                <a style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
                                  onClick={() => openPaymentContact()}>
                                  {detail.paymentContact}
                                </a>
                              </b>
                            )}
                          </td>
                          <td width="15%" style={{ whiteSpace: "nowrap" }}>&nbsp;</td>
                          <td width="15%" style={{ whiteSpace: "nowrap" }}>
                            &nbsp;Balance Payment Option:&nbsp;
                            <span style={{ color: "#409FFF" }}>{detail.getBalPayOpt}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

                {/* ── Two-column main layout ───────────────────── */}
                <tr>
                  {/* LEFT COLUMN */}
                  <td style={{ width: "55%", verticalAlign: "top" }}>
                    <table border={0} cellPadding={3} cellSpacing={0} style={{ width: "100%" }}>
                      <tbody>
                        <tr>
                          <td style={{ width: "65%" }}>
                            <b>{detail.schoolCode}</b>&nbsp;&nbsp;Distributor
                          </td>
                          <td style={{ width: "55%" }}>&nbsp;{detail.salesman}</td>
                        </tr>

                        <tr>
                          <td><b>Primary Contact Surname</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }} maxLength={50}
                              value={form.surname || ""}
                              onChange={(e) => patch("surname", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Primary Contact Name</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }}
                              value={form.firstName || ""}
                              onChange={(e) => patch("firstName", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Address</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }} maxLength={100}
                              value={form.address || ""}
                              onChange={(e) => patch("address", e.target.value)} />
                          </td>
                        </tr>

                        {/* State */}
                        <tr>
                          <td><b>{isChina ? "Province" : "State"}</b></td>
                          {!isChina ? (
                            <td style={{ display: "" }}>
                              <select className="cssDropDownList" style={{ width: "98%" }}
                                value={form.state || ""}
                                onChange={(e) => handleStateChange(e.target.value)}>
                                <option value="">--Select--</option>
                                {form.state && !stateOptions.some((o) => o.value === form.state) && (
                                  <option value={form.state}>{form.state}</option>
                                )}
                                {stateOptions.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            </td>
                          ) : (
                            <td style={{ display: "" }}>
                              <input className="cssTextBox" style={{ width: "95%" }} maxLength={50}
                                value={form.state || ""}
                                onKeyPress={(e) => { if (!isAlphaOnly(e)) e.preventDefault(); }}
                                onChange={(e) => patch("state", e.target.value)} />
                            </td>
                          )}
                        </tr>

                        {/* Suburb */}
                        <tr>
                          <td><b>{isChina ? "City" : "Suburb"}</b></td>
                          {!isChina ? (
                            <td style={{ display: "" }}>
                              <input className="cssReadOnly" style={{ ...readOnlyCss, width: "95%" }}
                                readOnly value={form.city || ""} />
                              &nbsp;
                              <a style={{ cursor: "pointer", color: "blue", textDecoration: "underline", fontSize: 10 }}
                                onClick={openSuburbSelect}>
                                Change
                              </a>
                            </td>
                          ) : (
                            <td style={{ display: "" }}>
                              <input className="cssTextBox" style={{ width: "95%" }} maxLength={50}
                                value={form.city || ""}
                                onKeyPress={(e) => { if (!isAlphaOnly(e)) e.preventDefault(); }}
                                onChange={(e) => patch("city", e.target.value)} />
                            </td>
                          )}
                        </tr>

                        <tr>
                          <td><b>Post Code</b></td>
                          <td>
                            <input className="cssReadOnly" style={{ ...readOnlyCss, width: "95%" }}
                              readOnly maxLength={50} value={form.postalCode || ""} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Country</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }}
                              readOnly maxLength={25} value={form.country || detail.country} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Res. Phone</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: 25 }} maxLength={2}
                              value={form.areaCode || ""}
                              onChange={(e) => patch("areaCode", e.target.value)} />
                            &nbsp;
                            <input className="cssTextBox" style={{ width: "60%" }} maxLength={10}
                              value={form.phone || ""}
                              onChange={(e) => patch("phone", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Father Name</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }} maxLength={25}
                              value={form.fatherName || ""}
                              onChange={(e) => patch("fatherName", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Father SurName</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }} maxLength={25}
                              value={form.fatherSurName || ""}
                              onChange={(e) => patch("fatherSurName", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Mother Name</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }} maxLength={25}
                              value={form.motherName || ""}
                              onChange={(e) => patch("motherName", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Mother SurName</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }} maxLength={25}
                              value={form.motherSurName || ""}
                              onChange={(e) => patch("motherSurName", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Primary Contact</b></td>
                          <td>
                            <select style={{ width: 82 }}
                              value={form.primaryContact || "F"}
                              onChange={(e) => patch("primaryContact", e.target.value)}>
                              <option value="F">Father</option>
                              <option value="M">Mother</option>
                            </select>
                          </td>
                        </tr>

                        <tr>
                          <td><b>Mobile Dad</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }} maxLength={25}
                              value={form.mobile1 || ""}
                              onChange={(e) => patch("mobile1", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Mobile Mum</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }} maxLength={25}
                              value={form.mobile2 || ""}
                              onChange={(e) => patch("mobile2", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Father Off. Phone</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "10%" }} maxLength={2}
                              onKeyPress={(e) => { if (!isDecimalKey(e)) e.preventDefault(); }}
                              value={form.fatherOfficeAC || ""}
                              onChange={(e) => patch("fatherOfficeAC", e.target.value)} />
                            &nbsp;
                            <input className="cssTextBox" style={{ width: "50%" }} maxLength={10}
                              onKeyPress={(e) => { if (!isDecimalKey(e)) e.preventDefault(); }}
                              value={form.fatherOfficePhone || ""}
                              onChange={(e) => patch("fatherOfficePhone", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Mother Off. Phone</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "10%" }} maxLength={2}
                              onKeyPress={(e) => { if (!isDecimalKey(e)) e.preventDefault(); }}
                              value={form.motherOfficeAC || ""}
                              onChange={(e) => patch("motherOfficeAC", e.target.value)} />
                            &nbsp;
                            <input className="cssTextBox" style={{ width: "50%" }} maxLength={10}
                              onKeyPress={(e) => { if (!isDecimalKey(e)) e.preventDefault(); }}
                              value={form.motherOfficePhone || ""}
                              onChange={(e) => patch("motherOfficePhone", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>FamilyBackground</b></td>
                          <td><b>{detail.familyBackground}</b></td>
                        </tr>

                        <tr>
                          <td><b>Number of students</b></td>
                          <td>
                            <select style={{ width: 82 }} className="cssDropDownList"
                              value={form.noOfStudents || "1"}
                              onChange={(e) => patch("noOfStudents", e.target.value)}>
                              {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                          </td>
                        </tr>

                        <tr>
                          <td><b>School</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }} maxLength={50}
                              value={form.school || ""}
                              onChange={(e) => patch("school", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Date of Sale</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: 95 }} maxLength={10}
                              placeholder="dd/MM/yyyy"
                              value={form.dateOfSale || ""}
                              onChange={(e) => patch("dateOfSale", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>E-Mail</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "95%" }} maxLength={200}
                              title="Use ; to separate multiple eMail IDs"
                              value={form.email0 || ""}
                              onBlur={() => {
                                if (form.email0 && !isValidEmail(form.email0)) {
                                  showAlert("Please enter valid Email.", "A");
                                }
                              }}
                              onChange={(e) => patch("email0", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Salesman</b></td>
                          <td><b>{detail.salesman}</b></td>
                        </tr>

                        <tr>
                          <td><b>Type Of Sale</b></td>
                          <td>
                            <select style={{ width: "50%" }}
                              value={form.typeOfSaleValue || ""}
                              onChange={(e) => patch("typeOfSaleValue", e.target.value)}>
                              <option value="">-Select</option>
                              {typeOfSaleOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>

                        <tr>
                          <td><b>Support Fee</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: "65%" }} maxLength={10}
                              onKeyPress={(e) => { if (!isDecimalKey(e)) e.preventDefault(); }}
                              value={form.supportFee || ""}
                              onChange={(e) => patch("supportFee", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>No. of months Free Support</b></td>
                          <td>
                            <input className="cssTextBox" style={{ width: 70 }} maxLength={5}
                              onKeyPress={(e) => { if (!isNumeralsOnly(e)) e.preventDefault(); }}
                              value={form.freeSupportMonths || ""}
                              onChange={(e) => patch("freeSupportMonths", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <b>Support Fee Payment date</b>
                          </td>
                          <td>
                            <input className="cssTextBox" style={{ width: 85 }} maxLength={10}
                              placeholder="dd/MM/yyyy"
                              value={form.supportFeePayDate || ""}
                              onChange={(e) => patch("supportFeePayDate", e.target.value)} />
                          </td>
                        </tr>

                        <tr>
                          <td><b>Order date</b></td>
                          <td>&nbsp;{detail.orderDate}</td>
                        </tr>

                        <tr>
                          <td><b>Purchase Order No.</b></td>
                          <td>&nbsp;{detail.orderRef}</td>
                        </tr>

                        <tr>
                          <td><b>Sent date</b></td>
                          <td>&nbsp;{detail.deliveryDate}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>

                  {/* RIGHT COLUMN */}
                  <td style={{ width: "45%", verticalAlign: "top" }}>
                    <table border={0} cellPadding={3} cellSpacing={0} style={{ width: "100%" }}>
                      <tbody>
                        {/* Products header */}
                        <tr id="trProductsHeader"
                          style={{ display: packages.length ? "" : "none", backgroundColor: "#AAD5FF" }}>
                          <td style={{ borderRight: "1px solid black", borderTop: "1px solid black", fontSize: 11 }}
                            align="center">
                            Packages&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                            {detail.displayModifyPkg && (
                              <b>
                                <a style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
                                  onClick={openSelectPackages}>
                                  Modify Packages
                                </a>
                              </b>
                            )}
                          </td>
                          <td style={{ borderRight: "1px solid black", borderTop: "1px solid black", fontSize: 11 }}
                            align="center">Price</td>
                        </tr>

                        {/* Package rows */}
                        <tr>
                          <td colSpan={2}>
                            <div id="divProducts" style={{ width: "100%", overflowX: "hidden" }}>
                              {packages.length > 0 && (
                                <table id="tblProd" border={0} cellPadding={5} cellSpacing={0}
                                  style={{ width: "100%", borderLeft: "1px solid black", borderBottom: "1px solid black" }}>
                                  <tbody>
                                    {packages.map((pkg, i) => (
                                      <tr key={i}>
                                        <td style={{ borderRight: "1px solid black", borderTop: "1px solid black" }}>
                                          {pkg.name}
                                        </td>
                                        <td style={{ borderRight: "1px solid black", borderTop: "1px solid black" }}
                                          align="right">
                                          {pkg.price.toFixed(2)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Products footer */}
                        <tr id="trProductsFotter"
                          style={{ display: packages.length ? "" : "none", backgroundColor: "#AAD5FF" }}>
                          <td align="center" style={{ borderRight: "1px solid black", borderTop: "1px solid black", fontSize: 11 }}>
                            Total&nbsp;&nbsp;&nbsp;
                          </td>
                          <td id="tdProdTotal" align="right"
                            style={{ borderRight: "1px solid black", borderTop: "1px solid black", fontSize: 11, paddingRight: 8 }}>
                            <span id="lblProductsTotal">
                              {packages.reduce((s, p) => s + p.price, 0).toFixed(2)}
                            </span>
                          </td>
                        </tr>

                        {/* Type of Product (hidden for China per VB ChinaDisplay) */}
                        {!isChina && (
                          <tr>
                            <td colSpan={2}>
                              <b>
                                Type of Product&nbsp;
                                <span style={{ color: "red", fontSize: "medium" }}>*</span>
                              </b>
                              &nbsp;&nbsp;
                              <input type="checkbox" id="chbDongleVersion"
                                checked={form.dongleVersion || false}
                                onChange={(e) => handleDongleVersionChange(e.target.checked)} />
                              &nbsp;Dongle Version&nbsp;
                              <input type="checkbox" id="chbWebVersion"
                                checked={form.webVersion || false}
                                onChange={(e) => {
                                  patch("webVersion", e.target.checked);
                                  recalcBalance();
                                }} />
                              &nbsp;Online Version
                            </td>
                          </tr>
                        )}

                        {/* Dongle section (trDonglesDisplay – display mirrors stUpdate) */}
                        {(form.showDongles || form.dongleVersion) && (
                          <tr id="trDonglesDisplay">
                            <td colSpan={2} align="center">
                              <DongleTable
                                rows={dongleRows}
                                onChange={setDongleRows}
                                onValidate={validateDongle}
                                onCheckDamaged={checkDongleDamaged}
                                showAlert={showAlert}
                              />
                              <div style={{ marginTop: 4, textAlign: "right" }}>
                                <button className="cssButton"
                                  style={{ fontSize: 12, height: 34, border: "1px solid white" }}
                                  onClick={handleSaveDongles}>
                                  Save Dongles
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Extra Hardlocks (trExtraHL – only for dongle version) */}
                        {(form.dongleVersion) && (
                          <tr id="trExtraHL">
                            <td align="left">
                              Dongles:&nbsp;
                              <select id="ddlExtraHardLocks" className="cssDropDownList"
                                style={{ width: 130 }}
                                value={form.extraHardLocks || "0"}
                                onChange={(e) => {
                                  patch("extraHardLocks", e.target.value);
                                  const count = parseInt(e.target.value) || 0;
                                  const pricePerHL = detail.defaultExtraHLPrice || 75;
                                  patch("extraHardLocksPrice", (count * pricePerHL).toFixed(2));
                                  recalcBalance();
                                }}>
                                <option value="0">Extra Dongles</option>
                                {[1, 2, 3, 4, 5, 6].map((n) => (
                                  <option key={n} value={n}>{n}</option>
                                ))}
                              </select>
                              &nbsp;&nbsp;
                            </td>
                            <td align="right" style={{ paddingTop: 10 }}>
                              <input id="txtExtraHardLocksPrice"
                                readOnly
                                className="cssReadOnly"
                                style={{ ...readOnlyCss, border: "1px", textAlign: "right", fontWeight: "bold", width: 100 }}
                                maxLength={10}
                                value={form.extraHardLocksPrice || "0.00"} />
                            </td>
                          </tr>
                        )}

                        {/* Computer included */}
                        <tr>
                          <td>
                            <b>Computer included</b>&nbsp;
                            <input type="checkbox" id="chkAddComp"
                              checked={form.computerIncluded || false}
                              onChange={(e) => {
                                // fn_AddComp mirrors VB – shows alert "Cannot change"
                                // if already ordered. We allow toggle for new sales.
                                patch("computerIncluded", e.target.checked);
                                if (!e.target.checked) patch("computerPrice", "");
                                recalcBalance();
                              }} />
                            <br />
                            {form.computerIncluded && (
                              <span>
                                <label>
                                  <input type="radio" name="rdoComp" value="1"
                                    checked={(form.computerSO || "") === "1"}
                                    onChange={() => patch("computerSO", "1")} />
                                  &nbsp;HO to Organise
                                </label>
                                <br />
                                <label>
                                  <input type="radio" name="rdoComp" value="0"
                                    checked={(form.computerSO || "") === "0"}
                                    onChange={() => patch("computerSO", "0")} />
                                  &nbsp;Sales office to organise
                                </label>
                              </span>
                            )}
                          </td>
                          <td align="right" style={{ paddingTop: 10, verticalAlign: "top" }}>
                            {form.computerIncluded && (
                              <input id="txtComputerPrice"
                                readOnly
                                className="cssReadOnly"
                                style={{ ...readOnlyCss, border: "1px", textAlign: "right", fontWeight: "bold", width: 100 }}
                                maxLength={10}
                                value={form.computerPrice || ""} />
                            )}
                          </td>
                        </tr>

                        {/* Special Instruction */}
                        <tr>
                          <td style={{ verticalAlign: "top" }}>
                            <b>Special Instruction :</b>
                            <textarea className="cssTextBox"
                              style={{ height: 50, width: "70%" }}
                              value={form.specialInstruction || ""}
                              onChange={(e) => patch("specialInstruction", e.target.value)} />
                          </td>
                        </tr>

                        {/* Additional Discount */}
                        <tr style={{ textAlign: "right" }}>
                          <td>Additional Discount:</td>
                          <td align="right">
                            <input
                              id="txtAdditionalDiscount"
                              className={isInstalled ? "cssReadOnly" : "cssTextBox"}
                              readOnly={isInstalled}
                              style={{ fontWeight: "bold", color: "Black", textAlign: "right", width: 120, ...(isInstalled ? readOnlyCss : {}) }}
                              maxLength={8}
                              value={additionalDiscount}
                              onKeyPress={(e) => { if (!isDecimalKey(e)) e.preventDefault(); }}
                              onChange={(e) => setAdditionalDiscount(e.target.value)}
                              onBlur={recalcBalance}
                            />
                          </td>
                        </tr>

                        {/* BalanceStr – GST/discount/total/deposit/balance */}
                        <tr>
                          <td colSpan={2}>
                            <small style={{ color: "#555" }}>{detail.gstDisplay}</small>
                          </td>
                        </tr>
                        <tr>
                          <td align="right">Discount {detail.country === "Canada" ? "(exc taxes)" : ""}</td>
                          <td align="right">
                            <input type="text" id="txtDiscountAmount"
                              readOnly className="cssReadOnly"
                              style={{ ...readOnlyCss, textAlign: "right", fontWeight: "bold", width: 120 }}
                              value={discountAmt.toFixed(2)} />
                          </td>
                        </tr>
                        <tr>
                          <td align="right">
                            <b>Total {detail.country === "Canada" ? "(exc taxes)" : ""}</b>
                          </td>
                          <td align="right">
                            <input type="text" id="txtTotalExecAmount"
                              readOnly className="cssReadOnly"
                              style={{ ...readOnlyCss, textAlign: "right", fontSize: 12, fontWeight: "bold", width: 120 }}
                              value={formatMoney(totalExec)} />
                          </td>
                        </tr>
                        <tr>
                          <td align="right"><b>Total (inc taxes)</b></td>
                          <td align="right">
                            <input type="text" id="txtTotalAmount_IncTax"
                              readOnly className="cssReadOnly"
                              style={{ ...readOnlyCss, textAlign: "right", fontSize: 12, fontWeight: "bold", width: 120 }}
                              value={formatMoney(totalIncTax)} />
                          </td>
                        </tr>
                        <tr>
                          <td align="right">Deposit (inc taxes)</td>
                          <td align="right">
                            <input type="text" id="txtDepositAmount"
                              readOnly className="cssReadOnly"
                              style={{ ...readOnlyCss, textAlign: "right", fontWeight: "bold", width: 120 }}
                              value={formatMoney(depositAmt)} />
                          </td>
                        </tr>
                        <tr>
                          <td align="right"><b>Balance (inc taxes)</b></td>
                          <td align="right">
                            <input type="text" id="txtBalanceAmount"
                              readOnly className="cssReadOnly"
                              style={{ ...readOnlyCss, textAlign: "right", width: 120, fontSize: 12, fontWeight: "bold" }}
                              value={formatMoney(balanceAmt)} />
                          </td>
                        </tr>

                        {/* Online Reporting */}
                        <tr>
                          <td colSpan={2} style={{ height: 25 }}>
                            <b>Online Reporting</b>
                            <input type="radio" id="chkOnlineReporting"
                              name="radioA"
                              checked={form.onlineReporting || false}
                              onChange={() => handleReportingChange("Email")} />
                            {form.onlineReporting && (
                              <span id="sp_Emails">
                                &nbsp;&nbsp;Email:&nbsp;&nbsp;
                                <input id="txtEmail1" readOnly className="cssReadOnly"
                                  style={{ ...readOnlyCss, width: 190 }} maxLength={200}
                                  value={form.email0 || ""} />
                              </span>
                            )}
                          </td>
                        </tr>

                        {/* Mail Reporting */}
                        <tr>
                          <td colSpan={2} style={{ height: 15 }}>
                            <b>Mail Reporting</b>
                            <input type="radio" id="chkMailReporting"
                              name="radioA"
                              checked={form.mailReporting || false}
                              onChange={() => handleReportingChange("Address")} />
                            <br />
                            <textarea id="txtaMailAddr" className="cssTextBox"
                              style={{
                                height: 45, width: 190,
                                display: form.mailReporting ? "" : "none",
                              }}
                              maxLength={1000}
                              value={form.mailReportingAddress || ""}
                              onChange={(e) => patch("mailReportingAddress", e.target.value)} />
                          </td>
                        </tr>

                        {/* Hardlock # */}
                        {detail.hardlockNumber && (
                          <tr>
                            <td colSpan={2} style={{ verticalAlign: "top" }}>
                              <b>Hardlock #</b>&nbsp;&nbsp;&nbsp;&nbsp;
                              {detail.hardlockNumber.replace(/:/g, ", ")}&nbsp;&nbsp;
                              <a style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
                                onClick={openHardlock}>
                                Modify
                              </a>
                              <br />
                              <a style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
                                onClick={openHardlock}>
                                Create New Hardlock Invoice
                              </a>
                            </td>
                          </tr>
                        )}

                        {/* Students Details */}
                        <tr>
                          <td colSpan={2}>
                            <b>
                              Students Details&nbsp;
                              <a style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
                                onClick={openFamilyUsers}>
                                Add/Edit Family Users
                              </a>
                            </b>

                          </td>
                        </tr>

                        {/* Login info */}
                        <tr>
                          <td colSpan={2}>
                            <b>Login info:</b>&nbsp;&nbsp;&nbsp;
                            <i>Login :</i>&nbsp;{detail.loginId}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                            {/* Removed password on 03/11/2026 */}
                            {/* <i>Password :</i>&nbsp;{detail.password} */}
                          </td>
                        </tr>

                        {/* Testimonials */}
                        <tr>
                          <td>
                            <b>Testimonials : </b>&nbsp;
                            <select value={form.testimonials || "--Select-"}
                              onChange={(e) => patch("testimonials", e.target.value)}>
                              <option>--Select-</option>
                              <option>Yes</option>
                              <option>No</option>
                              <option>Not Yet</option>
                            </select>
                          </td>
                        </tr>

                        {/* Referrals */}
                        <tr>
                          <td>
                            <b>Referrals : </b>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                            <select value={form.referrals || "--Select--"}
                              onChange={(e) => patch("referrals", e.target.value)}>
                              <option>--Select--</option>
                              <option>Yes</option>
                              <option>No</option>
                              <option>Not Yet</option>
                            </select>
                          </td>
                        </tr>

                        {/* Family Info */}
                        <tr>
                          <td style={{ verticalAlign: "top" }}>
                            <b>Family Info: </b>
                            <br />
                            <textarea id="txtFamilyInfo" className="cssTextBox"
                              style={{ height: 45, width: 290 }}
                              maxLength={50}
                              value={form.familyInfo || ""}
                              onKeyPress={() => {
                                if ((form.familyInfo || "").length >= 50) {
                                  showAlert("Length cannot be more than 50 characters");
                                  return false;
                                }
                              }}
                              onChange={(e) => {
                                if (e.target.value.length > 50) {
                                  showAlert("Length cannot be more than 50 characters");
                                  return;
                                }
                                patch("familyInfo", e.target.value);
                              }} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

                {/* ── SaleApproved row – Cancellation comments + docket ── */}
                {/* {detail.saleApproved && (
                  <tr>
                    <td style={{ verticalAlign: "top", textAlign: "left", width: "35%" }}>
                      <span style={{ color: "Red", verticalAlign: "top" }}>
                        Cancellation comments
                      </span>
                      <span style={{ paddingLeft: 100, textAlign: "right" }}>
                        <textarea id="txtCancelComments" className="cssTextBox"
                          style={{ height: 45, width: "70%" }}
                          maxLength={200}
                          value={form.cancelComments || ""}
                          onChange={(e) => patch("cancelComments", e.target.value)} />
                      </span>
                    </td>
                    <td>
                      <b>Installation docket no. :</b>&nbsp;&nbsp;{detail.orderRef}&nbsp;&nbsp;&nbsp;
                      <a style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
                        onClick={printDeliveryDocket}>
                        Print docket
                      </a>
                    </td>
                  </tr>
                )} */}

                {/* ── Cancellation comments + docket ── */}
                <tr>
                  <td style={{ verticalAlign: "top", textAlign: "left", width: "35%" }}>
                    <span style={{ color: "red", verticalAlign: "top" }}>
                      Cancellation comments
                    </span>

                    <span style={{ paddingLeft: 100, textAlign: "right" }}>
                      <textarea
                        id="txtCancelComments"
                        className="cssTextBox"
                        style={{ height: 45, width: "70%" }}
                        maxLength={200}
                        value={form.cancelComments || ""}
                        onChange={(e) => patch("cancelComments", e.target.value)}
                      />
                    </span>
                  </td>

                  <td>
                    <b>Installation docket no. :</b>&nbsp;&nbsp;
                    {detail.orderRef}
                    &nbsp;&nbsp;&nbsp;

                    <a
                      style={{
                        cursor: "pointer",
                        color: "blue",
                        textDecoration: "underline",
                      }}
                      onClick={printDeliveryDocket}
                    >
                      Print docket
                    </a>
                  </td>
                </tr>

                {/* ── Documents + Installation ─────────────────── */}
                <tr>
                  <td>
                    {/* HOFollowUp */}


                    <div style={{ marginBottom: 6 }}>
                      <a
                        id="aFollowUp"
                        style={{
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          color: isDisabled ? "#999" : "#0000FF",
                          textDecoration: "underline",
                          fontWeight: "bold",
                          pointerEvents: isDisabled ? "none" : "auto",
                        }}
                        // onClick={!isDisabled ? openPreAudit : undefined}
                        onClick={openPreAudit}
                      >
                        <u><b>Pre-Audit Approved Documents</b></u>
                      </a>
                    </div>

                    {/* Finance link */}
                    {detail.isFinanceSale === "1" && (
                      <div style={{ marginBottom: 6 }}>
                        <a
                          href={
                            !isDisabled
                              ? `/FINANCE/EL_FinanceSales?SchoolCode=${detail.schoolCode}&FamilyCode=${detail.familyCode}&Login=${detail.login}&Previous=${urlPrevious}&FromPage=Modify&FilterType=${urlFilterType}${detail.isFinanceSale}&FromCourtesyCall=${urlFromCC}`
                              : undefined
                          }
                          style={{
                            color: isDisabled ? "#999" : "blue",
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            pointerEvents: isDisabled ? "none" : "auto",
                          }}
                        >
                          Modify Finance Records
                        </a>
                      </div>
                    )}
                    {detail.canModifyPayment && (
                      <div style={{ marginBottom: 6 }}>
                        <a
                          style={{
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            color: isDisabled ? "#999" : "#0000FF",
                            textDecoration: "underline",
                            fontWeight: "bold",
                            pointerEvents: isDisabled ? "none" : "auto",
                          }}
                          onClick={!isDisabled ? openModifyPayment : undefined}
                        >
                          <u><b>Modify Payment Method</b></u>
                        </a>
                      </div>
                    )}

                    <DocFlag ok={detail.documents.purchaseOrder} label="Purchase Order" />
                    <DocFlag ok={detail.documents.crelogixContract} label="Crelogix Contract" />
                    <DocFlag ok={detail.documents.padForm} label="PAD form" />
                    <DocFlag ok={detail.documents.voidCheque} label="Void Cheque(If applicable)" />
                    <DocFlag ok={detail.documents.studentEdge} label="Student Edge" />
                    <DocFlag ok={detail.documents.moneyBackGuarantee} label="Money Back Guarantee" />
                    <DocFlag ok={detail.documents.acknowledgementForm} label="Acknowledgement Form" />
                    <DocFlag ok={detail.documents.completedDeliveryDocket} label="Completed Delivery Docket" />

                    {/* Credits */}
                    {detail.credits.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <span
                          style={{
                            color: "red",
                            fontWeight: "bold",
                            textDecoration: "underline",
                          }}
                        >
                          Credits added
                        </span>
                        <table border={1} style={{ width: "100%", fontSize: 11 }} cellPadding={0} cellSpacing={0}>
                          <thead>
                            <tr style={{ fontWeight: "bold" }}>
                              <td>Added by</td><td>Date</td>
                              <td>Notes</td><td align="right">Amt.</td>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.credits.map((cr, ci) => (
                              <tr key={ci}>
                                <td>{cr.addedBy}</td>
                                <td>{cr.creditDate}</td>
                                <td>&nbsp;{cr.notes}</td>
                                <td align="right">{cr.creditAmount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <table style={{ width: "100%", fontSize: 11 }}>
                          <tbody>
                            <tr>
                              <td>Total Credit</td>
                              <td align="right" style={{ fontWeight: "bold" }}>
                                <input id="txtTotalCredit"
                                  readOnly className="cssTextBox"
                                  value={detail.totalCredit.toFixed(2)} />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </td>

                  <td style={{ verticalAlign: "top" }}>
                    <table border={0} cellPadding={2} cellSpacing={0} style={{ width: "100%" }}>
                      <tbody>
                        {/* Installation Date & Time */}
                        <tr>
                          <td style={{ width: "35%", whiteSpace: "nowrap" }}>
                            <b>Installation Date &amp; Time:&nbsp;
                              <b style={{ color: "Red" }}>*</b>&nbsp;&nbsp;
                            </b>
                          </td>
                          <td>
                            <input className={instDateDisabled ? "cssReadOnly" : "cssTextBox"}
                              id="txtInstallationDate"
                              style={{ width: 85, ...(instDateDisabled ? readOnlyCss : {}) }}
                              maxLength={10}
                              readOnly={instDateDisabled}
                              placeholder="dd/MM/yyyy"
                              value={form.installationDate || ""}
                              onChange={(e) => patch("installationDate", e.target.value)} />
                            &nbsp;&amp;&nbsp;
                            <input className={instDateDisabled ? "cssReadOnly" : "cssTextBox"}
                              id="txtInstallationTime"
                              style={{ width: 45, ...(instDateDisabled ? readOnlyCss : {}) }}
                              maxLength={5}
                              readOnly={instDateDisabled}
                              placeholder="HH:mm"
                              value={form.installationTime || ""}
                              onKeyPress={(e) => { if (!isTimeKey(e)) e.preventDefault(); }}
                              onChange={(e) => patch("installationTime", e.target.value)} />
                            &nbsp;&nbsp;eg: '23:59'&nbsp;&nbsp;
                            <span>{detail.installStatus}</span>
                          </td>
                        </tr>

                        {/* Installer */}
                        <tr>
                          <td style={{ whiteSpace: "nowrap" }}><b>Installer : </b></td>
                          <td>
                            <select id="ddlInstaller" className="cssDropDownList"
                              style={{ width: 150 }}
                              disabled={installerDisabled}
                              value={form.installerLogin || ""}
                              onChange={(e) => patch("installerLogin", e.target.value)}>
                              <option value="">--Select--</option>
                              {installerOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>

                        {/* CPC – T2121 */}
                        <tr>
                          <td style={{ whiteSpace: "nowrap" }}><b>CPC: </b></td>
                          <td>
                            <input type="checkbox" id="chkCPC"
                              disabled={cpcDisabled}
                              checked={form.cpc || false}
                              onChange={(e) => patch("cpc", e.target.checked)} />
                          </td>
                        </tr>

                        {/* Cancellation Reason – T2121 */}
                        <tr>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <b>Cancellation Reason: </b>
                          </td>
                          <td>
                            <select id="ddlCnclReason"
                              style={{ width: "50%" }}
                              disabled={cpcDisabled}
                              value={form.cancellationReason || ""}
                              onChange={(e) => patch("cancellationReason", e.target.value)}>
                              <option value="">-Select</option>
                              {cancelReasonOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ── tbl2: Action buttons ─────────────────────────────── */}
            <table
              id="tbl2"
              width="100%"
              cellPadding={3}
              border={0}
              cellSpacing={0}
              style={{
                position: "sticky",
                bottom: 0,
                zIndex: 1000,
                backgroundColor: "azure",
                borderTop: "1px solid gray",
                borderLeft: "1px solid gray",
                borderRight: "1px solid gray",
                borderBottom: "1px solid gray",
                boxShadow: "0 -3px 10px rgba(0,0,0,0.15)",
                marginTop: 10,
              }}
            >
              <tbody>
                <tr>
                  <td
                    colSpan={2}
                    align="center"
                    style={{
                      padding: "10px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {/* Staff Commission link (hidden per VB ShowStaffCommission) */}
                    {detail.showStaffCommission && (
                      <>
                        <a
                          id="hrefStaffCommission"
                          style={{
                            fontSize: 11,
                            verticalAlign: "top",
                            display: "none",
                          }}
                        >
                          Staff Commission
                        </a>
                        &nbsp;&nbsp;
                      </>
                    )}

                    {/* Reverse Cancellation */}
                    <button
                      id="btnRevCancel"
                      className="cssButton"
                      style={{
                        backgroundColor: "red",
                        color: "white",
                        fontWeight: "bold",
                        height: 30,
                        width: 170,
                        marginRight: 8,
                        opacity: 1,
                        cursor: "pointer",
                      }}
                      onClick={handleRevCancel}
                    >
                      Reverse Cancellation
                    </button>

                    {/* Cancel Sale */}
                    <button
                      id="btnCancel"
                      className="cssButton"
                      style={{
                        backgroundColor: "red",
                        color: "white",
                        fontWeight: "bold",
                        height: 30,
                        width: 110,
                        marginRight: 8,
                      }}
                      disabled={saving}
                      onClick={handleCancel}
                    >
                      Cancel Sale
                    </button>

                    {/* Save */}
                    <button
                      id="btnSave"
                      className="cssButton"
                      style={{
                        height: 30,
                        width: 90,
                        fontWeight: "bold",
                        marginRight: 8,
                      }}
                      disabled={saving}
                      onClick={handleSave}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>

                    {/* Back */}
                    <button
                      id="btnBack"
                      className="cssButton"
                      style={{
                        height: 30,
                        width: 90,
                        fontWeight: "bold",
                      }}
                      onClick={handleBack}
                    >
                      Back
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>
    </>
  );
}

declare let _: unknown;

function openPaymentContact() {
  // Handled inline in component via showAlert
}